import i18nFactory from '@yandex-int/i18n';
import produce, { Draft } from 'immer';
import { forEach, isInteger, isNaN, keys, mapValues, memoize, parseInt, uniq, values } from 'lodash';
import moment from 'moment';

import {
    getNotificationsValueFromNotificationsEditor,
    getTimeTargetingValueFromTimeTargetingEditor,
    mapVisitingCardEditCampaignEditorValueToVisitingCardEditCampaignValue
} from '../../mappers/campaignsEdit/campaignsEdit';
import { mapStringToCurrencySymbol } from '../../mappers/common/common';
import { mapEditorExperimentsToCampaignExperiments } from '../../mappers/experimentsEditor';
import { mapMinusPhrasesEditorValueToCampaignMinusPhrasesValue } from '../../mappers/minusPhrases';
import { EntityType, mapEntityIdToEditorKey } from '../../mappers/organization/organization';
import { mapEditorRateCorrectionsToRateCorrections } from '../../mappers/rateCorrections/rateCorrections';
import { mapRelevantPhrasesEditorValueToCampaignRelevantPhrasesValue } from '../../mappers/relevantPhrases';
import { mapStrategyForCampaignData } from '../../mappers/strategyEditor';
import { selectCampaignPlaceEditorData } from '../../selectors/campaignPlaceEditor/campaignPlaceEditor';
import {
    getCampaignData,
    getCampaignValue,
    selectEditCampaignsConstants,
    selectEnabledFieldsForCampaign
} from '../../selectors/campaignsEdit/campaignsEdit';
import { selectCampaignServiceEditorData } from '../../selectors/campaignServiceEditor/campaignServiceEditor';
import { selectChatListEditorById } from '../../selectors/chatListEditor/chatListEditor';
import {
    selectEndDateFromDatesIntervalEditor,
    selectStartDateFromDatesIntervalEditor
} from '../../selectors/DatesInterval/DatesInterval';
import { selectEditPageItemNameEditorValue } from '../../selectors/editPageItemNameEditor/editPageItemNameEditor';
import { selectExperimentsEditorById } from '../../selectors/experimentsEditor/experimentsEditor';
import { selectItemsListTextareaEditorValue } from '../../selectors/ItemsListTextareaEditor/ItemsListTextareaEditor';
import { selectMetrikaCounterEditorById } from '../../selectors/metrikaCounterEditor/metrikaCounterEditor';
import {
    selectMetrikaKeyGoalsConstants,
    selectMetrikaKeyGoalsEditorById
} from '../../selectors/metrikaKeyGoalsEditor/metrikaKeyGoalsEditor';
import { selectMinusPhrasesEditorById } from '../../selectors/minusPhrases/minusPhrases';
import { selectNotificationsEditorValue } from '../../selectors/notificationsEditor/notificationsEditor';
import { selectRateCorrectionsEditorValue } from '../../selectors/rateCorrections/rateCorrections';
import { selectRelevantPhrasesEditorValue } from '../../selectors/relevantPhrasesEditor/relevantPhrasesEditor';
import { selectStrategyEditorData } from '../../selectors/strategyEditor/strategyEditor';
import { selectTimeTargetingEditorValue } from '../../selectors/timeTargetingEditor/timeTargetingEditor';
import {
    selectOrganizationsEditorValue,
    selectVisitingCardEditorValue
} from '../../selectors/visitingCardEditor/visitingCardEditor';
import { CampaignPlace } from '../../typings/campaignPlace';
import { CampaignPlaceEditorError } from '../../typings/campaignPlaceEditor';
import {
    CampaignsEditEditorsValidationResult,
    EditCampaignData,
    EditCampaignEditor,
    EditCampaignValueField,
    EnabledFieldsMap
} from '../../typings/campaignsEdit';
import { CampaignService } from '../../typings/campaignService';
import { ValidationErrorData } from '../../typings/common';
import { CampaignPlatform, CampaignType } from '../../typings/domain';
import { OperatorFeaturesFragment } from '../../typings/graphql-grid';
import { MetrikaKeyGoalsEditorValidationResult } from '../../typings/metrikaKeyGoals';
import { NotificationsEditorValidationData } from '../../typings/notificationEditor';
import { RateCorrectionType } from '../../typings/ratesCorrections';
import { ClientFeatures, State } from '../../typings/state';
import { StrategyForEditor } from '../../typings/strategy';
import { parseAllowedPageIdsEditorValue } from '../AllowedPageIdsEditorHelpers/AllowedPageIdsEditorHelpers';
import { parseBannedDomainsEditorValue } from '../BannedDomainsEditorHelpers/BannedDomainsEditorHelpers';
import { parseDisabledIpsEditorValue } from '../DisabledIpsEditorHelpers/DisabledIpsEditorHelpers';
import { getArchivedExperimentsCount } from '../ExperimentsEditorHelpers/ExperimentsEditorHelpers';
import { validateKeyGoalsEditor } from '../MetrikaKeyGoalsEditorHelpers/MetrikaKeyGoalsEditorHelpers';
import { validateNotificationsEditor } from '../NotificationEditorHelper/NotificationEditorHelper';
import {
    RateCorrectionEditorValueValidationResult,
    validateRateCorrectionEditor
} from '../RateCorrectionsEditorHelpers/RateCorrectionsEditorHelpers';
import {
    RelevantPhrasesValidationData,
    validateRelevantPhrasesEditor
} from '../RelevantPhrasesHelpers/RelevantPhrasesHelpers';
import {
    isStrategyNeedsMarkMetrikaLinksForEditorEnabled,
    isStrategyOptimizeCpa
} from '../StrategyEditorHelpers/StrategyEditorHelpers';
import {
    isStrategyValidationResultPositive,
    validateCampaignStrategyEditor
} from '../StrategyEditorValidators/StrategyEditorValidators';
import {
    isTimeTargetingValidationResultPositive,
    validateCampaignTimeTargetingEditor
} from '../TimeTargetingEditorHelper/TimeTargetingEditorHelper';
import { isIPValid } from '../ValidationHelpers/ValidationHelpers';

import * as keyset from './CampaignsEditHelpers.i18n';

const i18n = i18nFactory(keyset);

export const checkIsCampaignAddAllowedInDna = (
    campaignType: CampaignType,
    operatorFeature: OperatorFeaturesFragment,
    clientFeatures: ClientFeatures
): boolean => {
    switch (campaignType) {
        case CampaignType.TEXT:
        case CampaignType.CONTENT_PROMOTION:
            return operatorFeature.isCampaignAddAllowed;
        case CampaignType.CPM_BANNER:
            return Boolean(clientFeatures.isCpmBannerCampaignAddAllowed);
        case CampaignType.CPM_PRICE:
            return true; // у CPM_PRICE нет фичи
        case CampaignType.INTERNAL_AUTOBUDGET:
            return true; // у INTERNAL_AUTOBUDGET нет фичи
        default:
            return false;
    }
};

export const checkIsCampaignUpdateAllowedInDna = (
    campaignType: CampaignType,
    operatorFeature: OperatorFeaturesFragment,
    clientFeatures: ClientFeatures
): boolean => {
    switch (campaignType) {
        case CampaignType.TEXT:
        case CampaignType.CONTENT_PROMOTION:
            return operatorFeature.isCampaignUpdateAllowed;
        case CampaignType.CPM_BANNER:
            return Boolean(clientFeatures.isCpmBannerCampaignUpdateAllowed);
        case CampaignType.CPM_PRICE:
            return true; // у CPM_PRICE нет фичи
        case CampaignType.INTERNAL_AUTOBUDGET:
            return true; // у INTERNAL_AUTOBUDGET нет фичи
        default:
            return false;
    }
};

export const METRIKA_COUNTERS_DEPENDABLE_FIELDS = [
    EditCampaignValueField.experiments,
    EditCampaignValueField.metrikaKeyGoals
];

export const getMetrikaContersDependableFieldsWarningText = (
    dependabledFields: EditCampaignValueField[]
): string | undefined => {
    const dependableFieldsNames = {
        [EditCampaignValueField.experiments]: i18n('experiments'),
        [EditCampaignValueField.metrikaKeyGoals]: i18n('key-goals')
    };

    if (dependabledFields.length === 0) {
        return undefined;
    }

    return i18n('warning-about-metrika-counters', {
        fields: dependabledFields.reduce((res, field, index) => {
            let separator;

            if (index === 0) {
                separator = '';
            } else if (index === dependabledFields.length - 1) {
                separator = ' ' + i18n('and') + ' ';
            } else {
                separator = ', ';
            }

            return `${res}${separator}${dependableFieldsNames[field]}`;
        }, '')
    });
};

export const getCampaignEditPageItemEditorId = (campaignId: string): string => {
    return `campaign-${campaignId}`;
};

export const validateCampaignsEditEditors = (state: State): Array<CampaignsEditEditorsValidationResult> => {
    const errors: Array<CampaignsEditEditorsValidationResult> = [];

    values(state.editCampaigns.campaigns).forEach((campaign: EditCampaignData) => {
        const campaignId = campaign.id;
        const enabledFields = selectEnabledFieldsForCampaign(state, campaignId);

        Object.keys(enabledFields).forEach((field: EditCampaignValueField) => {
            const errorsBaseData = {
                campaignId
            };

            switch (field) {
                case EditCampaignValueField.name: {
                    const validationResult = validateCampaignNameEditor(campaignId, state);

                    if (validationResult.length !== 0) {
                        errors.push({
                            ...errorsBaseData,
                            editor: EditCampaignEditor.nameEditor,
                            errors: validationResult
                        });
                    }

                    break;
                }

                case EditCampaignValueField.bannedDomains: {
                    const { maxBannedDomainsCount } = selectEditCampaignsConstants(state);
                    const validationResult = validateCampaignBannedDomainsEditor(
                        campaignId,
                        state,
                        maxBannedDomainsCount
                    );

                    if (validationResult.length !== 0) {
                        errors.push({
                            ...errorsBaseData,
                            editor: EditCampaignEditor.bannedDomainsEditor,
                            errors: validationResult
                        });
                    }

                    break;
                }

                case EditCampaignValueField.disabledIps: {
                    const validationResult = validateCampaignDisabledIpsEditor(campaignId, state);

                    if (validationResult.length !== 0) {
                        errors.push({
                            ...errorsBaseData,
                            editor: EditCampaignEditor.disabledIpsEditor,
                            errors: validationResult
                        });
                    }

                    break;
                }

                case EditCampaignValueField.relevantPhrases: {
                    const validationResult = validateCampaignRelevantPhrasesEditor(campaignId, state);

                    if (!validationResult.isValid) {
                        errors.push({
                            ...errorsBaseData,
                            editor: EditCampaignEditor.relevantPhrasesEditor,
                            errors: validationResult.errors
                        });
                    }

                    break;
                }

                case EditCampaignValueField.metrikaKeyGoals: {
                    const validationResult = validateMetrikaCampaignKeyGoalsEditor(campaignId, state);

                    if (!validationResult.isValid) {
                        errors.push({
                            ...errorsBaseData,
                            editor: EditCampaignEditor.metrikaKeyGoalsEditor,
                            keyGoalsValidationData: validationResult.keyGoalsValidationData
                        });
                    }

                    break;
                }

                case EditCampaignValueField.notifications: {
                    const validationResult = validateCampaignNotificationsEditor(campaignId, state);

                    if (!validationResult.isValid) {
                        errors.push({
                            ...errorsBaseData,
                            editor: EditCampaignEditor.notificationsEditor,
                            errors: validationResult
                        });
                    }

                    break;
                }

                case EditCampaignValueField.experiments: {
                    const isValid = validateCampaignExperimentsEditor(campaignId, state);

                    if (!isValid) {
                        errors.push({
                            ...errorsBaseData,
                            editor: EditCampaignEditor.experimentsEditor,
                            errors: { isValid }
                        });
                    }
                    break;
                }

                case EditCampaignValueField.strategy: {
                    const validationResult = validateCampaignStrategyEditor(campaignId, state);
                    if (!isStrategyValidationResultPositive(validationResult)) {
                        errors.push({
                            ...errorsBaseData,
                            editor: EditCampaignEditor.strategyEditor,
                            errors: validationResult
                        });
                    }
                    break;
                }

                case EditCampaignValueField.service: {
                    const serviceErrors = validateCampaignServiceEditor(campaignId, state);

                    if (serviceErrors.length) {
                        errors.push({
                            ...errorsBaseData,
                            editor: EditCampaignEditor.serviceEditor,
                            errors: serviceErrors
                        });
                    }

                    break;
                }

                case EditCampaignValueField.place: {
                    const placeErrors = validateCampaignPlaceEditor(campaignId, state);

                    if (placeErrors.length) {
                        errors.push({
                            ...errorsBaseData,
                            editor: EditCampaignEditor.placeEditor,
                            errors: placeErrors
                        });
                    }

                    break;
                }

                case EditCampaignValueField.metrikaCounters: {
                    const validationResult = validateCampaignMetrikaCountersEditor(campaignId, state);

                    if (validationResult.length !== 0) {
                        errors.push({
                            ...errorsBaseData,
                            editor: EditCampaignEditor.metrikaCounterEditor,
                            errors: validationResult
                        });
                    }

                    break;
                }

                case EditCampaignValueField.rateCorrections: {
                    const validationResult = validateCampaignRateCorrectionsEditor(campaignId, state);

                    if (!validationResult.isValid) {
                        errors.push({
                            ...errorsBaseData,
                            editor: EditCampaignEditor.rateCorrectionsEditor,
                            errors: validationResult.errors
                        });
                    }

                    break;
                }

                case EditCampaignValueField.allowedPageIds: {
                    const validationResult = validateCampaignAllowedPageIdsEditor(campaignId, state);

                    if (validationResult.length !== 0) {
                        errors.push({
                            ...errorsBaseData,
                            editor: EditCampaignEditor.allowedPageIdsEditor,
                            errors: validationResult
                        });
                    }

                    break;
                }

                case EditCampaignValueField.timeTargeting: {
                    const validationResult = validateCampaignTimeTargetingEditor(campaignId, state);

                    if (!isTimeTargetingValidationResultPositive(validationResult)) {
                        errors.push({
                            ...errorsBaseData,
                            editor: EditCampaignEditor.timeTargetingEditor,
                            errors: validationResult
                        });
                    }

                    break;
                }

                default:
                    break;
            }
        });
    });

    return errors;
};

export const getCampaignsDataWithOpenedEditors = (state: State): Record<string, EditCampaignData> => {
    return produce(state.editCampaigns.campaigns, (campaignsDraft: Draft<Record<string, EditCampaignData>>) => {
        forEach(campaignsDraft, (campaign: EditCampaignData) => {
            const campaignId = campaign.id;
            const enabledFields = selectEnabledFieldsForCampaign(state, campaignId);

            Object.keys(enabledFields).forEach((field: EditCampaignValueField) => {
                switch (field) {
                    case EditCampaignValueField.name: {
                        const editorId = getCampaignEditPageItemEditorId(campaignId);
                        const editorData = selectEditPageItemNameEditorValue(state.editPageItemNameEditor, editorId);

                        if (editorData.isVisible) {
                            campaign.value[EditCampaignValueField.name] = editorData.value;
                        }

                        break;
                    }

                    case EditCampaignValueField.dates: {
                        const startDate = selectStartDateFromDatesIntervalEditor(state, campaignId);
                        const endDate = selectEndDateFromDatesIntervalEditor(state, campaignId);

                        campaign.value[EditCampaignValueField.dates] = {
                            startDate: startDate.date,
                            endDate: endDate ? endDate.date : undefined
                        };

                        break;
                    }

                    case EditCampaignValueField.datesWithRequiredEndDate: {
                        const startDate = selectStartDateFromDatesIntervalEditor(state, campaignId);
                        const endDate = selectEndDateFromDatesIntervalEditor(state, campaignId);

                        campaign.value[EditCampaignValueField.datesWithRequiredEndDate] = {
                            startDate: startDate.date,
                            endDate: endDate ? endDate.date : startDate.date
                        };

                        break;
                    }

                    case EditCampaignValueField.relevantPhrases: {
                        const editorValue = selectRelevantPhrasesEditorValue(
                            state,
                            getCampaignEditPageItemEditorId(campaignId)
                        );

                        if (editorValue.isVisible) {
                            campaign.value[
                                EditCampaignValueField.relevantPhrases
                            ] = mapRelevantPhrasesEditorValueToCampaignRelevantPhrasesValue(editorValue);
                        }

                        break;
                    }

                    case EditCampaignValueField.disabledIps: {
                        const { isVisible, value } = selectItemsListTextareaEditorValue(
                            state.itemsListTextareaEditor,
                            getCampaignDisabledIpsEditorId(campaignId)
                        );

                        if (isVisible) {
                            campaign.value[EditCampaignValueField.disabledIps] = parseDisabledIpsEditorValue(value);
                        }

                        break;
                    }

                    case EditCampaignValueField.chatOperator: {
                        const { isOpen, selectedChatId } = selectChatListEditorById(
                            state,
                            getCampaignChatListEditorId(campaignId)
                        );

                        if (isOpen) {
                            campaign.value[EditCampaignValueField.chatOperator] = {
                                selectedChatId
                            };
                        }

                        break;
                    }

                    case EditCampaignValueField.bannedDomains: {
                        const { isVisible, value } = selectItemsListTextareaEditorValue(
                            state.itemsListTextareaEditor,
                            getCampaignBannedDomainsEditorId(campaignId)
                        );

                        if (isVisible) {
                            campaign.value[EditCampaignValueField.bannedDomains] = parseBannedDomainsEditorValue(value);
                        }

                        break;
                    }

                    case EditCampaignValueField.allowedPageIds: {
                        const { isVisible, value } = selectItemsListTextareaEditorValue(
                            state.itemsListTextareaEditor,
                            getCampaignAllowedPageIdsEditorId(campaignId)
                        );

                        if (isVisible) {
                            campaign.value[EditCampaignValueField.allowedPageIds] = parseAllowedPageIdsEditorValue(
                                value
                            );
                        }

                        break;
                    }

                    case EditCampaignValueField.notifications: {
                        const editorId = getCampaignEditPageItemEditorId(campaignId);
                        const notificationEditorValue = selectNotificationsEditorValue(state, editorId);

                        if (notificationEditorValue.isOpen) {
                            campaign.value[
                                EditCampaignValueField.notifications
                            ] = getNotificationsValueFromNotificationsEditor(notificationEditorValue);
                        }

                        break;
                    }

                    case EditCampaignValueField.metrikaCounters: {
                        const editorId = getCampaignEditPageItemEditorId(campaignId);
                        const { isOpen, selectedCounters } = selectMetrikaCounterEditorById(state, editorId);

                        if (isOpen) {
                            campaign.value[EditCampaignValueField.metrikaCounters] = selectedCounters || [];
                        }

                        break;
                    }

                    case EditCampaignValueField.markMetrikaLinks: {
                        const metrikaEditorId = getCampaignEditPageItemEditorId(campaignId);
                        const strategyEditorId = getCampaignStrategyEditorId(campaignId);
                        const { isOpen, markMetrikaLinks } = selectMetrikaCounterEditorById(state, metrikaEditorId);
                        const strategy = selectStrategyEditorData(state, strategyEditorId);
                        const isMarkMetrikaLinkEnabledByStrategy = isStrategyNeedsMarkMetrikaLinksForEditorEnabled(
                            strategy
                        );

                        if (isOpen) {
                            campaign.value[EditCampaignValueField.markMetrikaLinks] =
                                isMarkMetrikaLinkEnabledByStrategy || markMetrikaLinks;
                        } else {
                            campaign.value[EditCampaignValueField.markMetrikaLinks] =
                                isMarkMetrikaLinkEnabledByStrategy ||
                                campaign.value[EditCampaignValueField.markMetrikaLinks];
                        }

                        break;
                    }

                    case EditCampaignValueField.rateCorrections: {
                        const editorId = getCampaignEditPageItemEditorId(campaignId);
                        const { isVisible, value } = selectRateCorrectionsEditorValue(state, editorId);

                        if (isVisible) {
                            campaign.value[
                                EditCampaignValueField.rateCorrections
                            ] = mapEditorRateCorrectionsToRateCorrections(value);
                        }

                        break;
                    }

                    case EditCampaignValueField.experiments: {
                        const editorData = selectExperimentsEditorById(state.experimentsEditor, campaignId);

                        if (editorData.isOpen) {
                            const experimentsValue = campaign.value[EditCampaignValueField.experiments];

                            campaign.value[EditCampaignValueField.experiments] = {
                                experimentsTree: campaign.value[EditCampaignValueField.experiments].experimentsTree,
                                ...mapEditorExperimentsToCampaignExperiments(editorData, experimentsValue)
                            };
                        }

                        break;
                    }

                    case EditCampaignValueField.metrikaKeyGoals: {
                        const editorData = selectMetrikaKeyGoalsEditorById(
                            state,
                            getCampaignMetrikaKeyGoalsEditorId(campaignId)
                        );

                        if (editorData.isOpen) {
                            campaign.value[EditCampaignValueField.metrikaKeyGoals].selectedGoals =
                                editorData.selectedGoals;
                        }

                        break;
                    }

                    case EditCampaignValueField.strategy: {
                        const editorData = selectStrategyEditorData(state, getCampaignStrategyEditorId(campaignId));
                        const strategy = editorData.strategies[editorData.strategyType];

                        if (editorData.isOpen && strategy) {
                            campaign.value[EditCampaignValueField.strategy] = mapStrategyForCampaignData(strategy);
                        }

                        break;
                    }

                    case EditCampaignValueField.service: {
                        const editorData = selectCampaignServiceEditorData(
                            state,
                            getCampaignServiceEditorId(campaignId)
                        );

                        if (editorData.isOpen) {
                            campaign.value[EditCampaignValueField.service] = {
                                serviceId: editorData.serviceId,
                                isMobile: editorData.isMobile
                            };
                        }

                        break;
                    }

                    case EditCampaignValueField.place: {
                        const editorData = selectCampaignPlaceEditorData(state, getCampaignPlaceEditorId(campaignId));

                        if (editorData.isOpen) {
                            campaign.value[EditCampaignValueField.place] = {
                                pageId: editorData.pageId,
                                placeId: editorData.placeId
                            };
                        }

                        break;
                    }

                    case EditCampaignValueField.minusPhrases: {
                        const editorData = selectMinusPhrasesEditorById(
                            state,
                            getCampaignMinusPhrasesEditorId(campaignId)
                        ).minusKeywordsEditor;

                        if (editorData.isOpen) {
                            campaign.value[EditCampaignValueField.minusPhrases] = {
                                ...mapMinusPhrasesEditorValueToCampaignMinusPhrasesValue(
                                    editorData,
                                    state.minusPhraseLibraryEditor.libraryItems || []
                                )
                            };
                        }

                        break;
                    }

                    case EditCampaignValueField.timeTargeting: {
                        const editorData = selectTimeTargetingEditorValue(state, campaignId);

                        if (editorData.isOpened) {
                            campaign.value[
                                EditCampaignValueField.timeTargeting
                            ] = getTimeTargetingValueFromTimeTargetingEditor(editorData);
                        }

                        break;
                    }

                    case EditCampaignValueField.visitingCard: {
                        const editorData = selectVisitingCardEditorValue(
                            state,
                            getCampaignEditPageEditorId(campaignId)
                        );

                        if (editorData.isOpen) {
                            campaign.value[EditCampaignValueField.visitingCard] = {
                                ...mapVisitingCardEditCampaignEditorValueToVisitingCardEditCampaignValue(editorData)
                            };
                        }

                        break;
                    }

                    case EditCampaignValueField.organizationId: {
                        const editorData = selectOrganizationsEditorValue(
                            state,
                            mapEntityIdToEditorKey(campaignId, EntityType.campaign)
                        );

                        if (editorData.isOpen) {
                            campaign.value[EditCampaignValueField.organizationId] = editorData.organizationId;
                        }

                        break;
                    }

                    default:
                        break;
                }
            });
        });

        return campaignsDraft;
    });
};

export const validateCampaignNameEditor = (campaignId: string, state: State): ValidationErrorData[] => {
    const editorId = getCampaignEditPageItemEditorId(campaignId);
    const editorData = selectEditPageItemNameEditorValue(state.editPageItemNameEditor, editorId);
    const constants = selectEditCampaignsConstants(state);
    const { maxNameLength } = constants;

    if (!editorData.isVisible) {
        return [];
    }

    return validateCampaignName(editorData.value, maxNameLength);
};

export const validateCampaignName = (value: string, maxLengthValue: number): ValidationErrorData[] => {
    const errors: ValidationErrorData[] = [];

    if (value.trim() === '') {
        errors.push({
            code: 'EMPTY_NAME',
            text: i18n('empty-campaign-name')
        });
    }

    if (value.length > maxLengthValue) {
        errors.push({
            code: 'MAX_LENGTH_NAME',
            text: i18n('max-length-name', {
                count: maxLengthValue
            })
        });
    }

    return errors;
};

export const validateCampaignBannedDomainsEditor = (
    campaignId: string,
    state: State,
    bannedDomainsLimit: number
): ValidationErrorData[] => {
    const editorId = getCampaignBannedDomainsEditorId(campaignId);
    const { isVisible, value } = selectItemsListTextareaEditorValue(state.itemsListTextareaEditor, editorId);

    if (!isVisible) {
        return [];
    }

    return validateCampaignBannedDomains(parseBannedDomainsEditorValue(value), bannedDomainsLimit);
};

export const validateCampaignBannedDomains = (
    bannedDomains: string[],
    bannedDomainsLimit: number
): ValidationErrorData[] => {
    const errors = [];

    if (bannedDomains.length > bannedDomainsLimit) {
        errors.push({
            code: 'BANNED_DOMAINS_LIMIT_REACHED',
            text: i18n('banned-domains-limit-reached', {
                limit: bannedDomainsLimit
            })
        });
    }

    return errors;
};

export const validateCampaignAllowedPageIdsEditor = (campaignId: string, state: State): ValidationErrorData[] => {
    const editorId = getCampaignAllowedPageIdsEditorId(campaignId);
    const { isVisible, value } = selectItemsListTextareaEditorValue(state.itemsListTextareaEditor, editorId);
    const { maxAllowedPageIdsCount } = selectEditCampaignsConstants(state);

    if (!isVisible) {
        return [];
    }

    return validateCampaignAllowedPageIds(parseAllowedPageIdsEditorValue(value), maxAllowedPageIdsCount);
};

export const validateCampaignAllowedPageIds = (
    allowedPageIds: string[],
    maxAllowedPageIdsCount: number
): ValidationErrorData[] => {
    const errors = [];

    if (allowedPageIds.length > maxAllowedPageIdsCount) {
        errors.push({
            code: 'ALLOWED_PAGE_IDS_LIMIT_REACHED',
            text: i18n('allowed_page-ids-limit-reached', {
                limit: maxAllowedPageIdsCount
            })
        });
    }

    const allowedPagedIdsWithNotNumericPositiveValue = allowedPageIds.filter(allowedPageId => {
        const parseValue = Number(allowedPageId);
        return isNaN(parseValue) || parseValue <= 0 || !isInteger(parseValue);
    });

    if (allowedPagedIdsWithNotNumericPositiveValue.length > 0) {
        errors.push({
            code: 'ALLOWED_PAGE_ID_IS_NOT_NUMBER',
            text: i18n('allowed-page-id-is-not-number', {
                values: allowedPagedIdsWithNotNumericPositiveValue.join(', ')
            })
        });
    }

    return errors;
};

export const validateCampaignDisabledIpsEditor = (campaignId: string, state: State): ValidationErrorData[] => {
    const editorId = getCampaignDisabledIpsEditorId(campaignId);
    const { isVisible, value } = selectItemsListTextareaEditorValue(state.itemsListTextareaEditor, editorId);

    if (!isVisible) {
        return [];
    }

    const maxCampaignDisabledIpsNumber = state.editCampaigns.constants
        ? state.editCampaigns.constants.maxDisabledIpsCount
        : undefined;

    return validateCampaignDisabledIps(parseDisabledIpsEditorValue(value), maxCampaignDisabledIpsNumber);
};

export const validateCampaignDisabledIps = (ips: string[], maxIpsNumber?: number): ValidationErrorData[] => {
    const errors = [];

    if (maxIpsNumber !== undefined && ips.length > maxIpsNumber) {
        errors.push({
            code: 'DISABLED_IPS_MAX_LENGTH',
            text: i18n('invalid-disabled-ips-number', { maxIpsNumber })
        });
    }

    const invalidIps = ips.filter(ip => {
        return isIPValid(ip) === false;
    });

    if (invalidIps.length !== 0) {
        errors.push({
            code: 'INVALID_IPS',
            text: i18n('invalid-ips', { ips: invalidIps.join(', ') })
        });
    }

    return errors;
};

export const validateMetrikaCampaignKeyGoalsEditor = (
    campaignId: string,
    state: State
): MetrikaKeyGoalsEditorValidationResult => {
    const editorId = getCampaignMetrikaKeyGoalsEditorId(campaignId);
    const { workCurrency } = state.commonData.client;
    const metrikaKeyGoalsEditorState = selectMetrikaKeyGoalsEditorById(state, editorId);

    const { involvedSessionsId } = selectEditCampaignsConstants(state);
    const constants = selectMetrikaKeyGoalsConstants(state);

    const { availableForKeyGoals = {} } = getCampaignData(state, campaignId);

    return validateKeyGoalsEditor(
        metrikaKeyGoalsEditorState.isOpen
            ? metrikaKeyGoalsEditorState.selectedGoals
            : getCampaignValue(state, campaignId)[EditCampaignValueField.metrikaKeyGoals].selectedGoals,
        constants,
        mapStringToCurrencySymbol(workCurrency),
        availableForKeyGoals,
        involvedSessionsId
    );
};

export const validateCampaignRelevantPhrasesEditor = (
    campaignId: string,
    state: State
): RelevantPhrasesValidationData => {
    const editorId = getCampaignEditPageEditorId(campaignId);
    const campaignData = getCampaignData(state, campaignId);
    const { relevantPhrasesGoals } = campaignData;
    const relevantPhrasesEditorData = selectRelevantPhrasesEditorValue(state, editorId);

    const goals = relevantPhrasesGoals ? relevantPhrasesGoals.goalsData : {};
    const { minRelevantPhrasesLimit, maxRelevantPhrasesLimit } = selectEditCampaignsConstants(state);

    const validationErrors = validateRelevantPhrasesEditor(
        relevantPhrasesEditorData,
        {
            minLimit: minRelevantPhrasesLimit,
            maxLimit: maxRelevantPhrasesLimit
        },
        goals
    );

    return {
        isValid: validationErrors.limit.length === 0 && validationErrors.goal.length === 0,
        errors: validationErrors
    };
};

export const validateCampaignNotificationsEditor = (
    campaignId: string,
    state: State
): NotificationsEditorValidationData => {
    const editorId = getCampaignEditPageEditorId(campaignId);
    const notificationEditorState = selectNotificationsEditorValue(state, editorId);

    if (!notificationEditorState.isOpen) {
        return {
            common: {},
            notifications: [],
            isValid: true
        };
    }

    return validateNotificationsEditor(notificationEditorState, state.commonData.constants);
};

export const validateCampaignRateCorrectionsEditor = (
    campaignId: string,
    state: State
): RateCorrectionEditorValueValidationResult => {
    const editorId = getCampaignEditPageEditorId(campaignId);
    const editorState = selectRateCorrectionsEditorValue(state, editorId);

    if (!editorState.isVisible) {
        return {
            isValid: true,
            errors: mapValues(RateCorrectionType, () => ({
                isValid: true,
                items: {},
                general: [],
                errorsSingleMassRateControl: []
            }))
        };
    }

    return validateRateCorrectionEditor(editorState.value);
};

export const validateCampaignExperimentsEditor = (campaignId: string, state: State): boolean => {
    const experimentsEditorState = selectExperimentsEditorById(state.experimentsEditor, campaignId);

    if (!experimentsEditorState.isOpen) {
        const experimentsValue = state.editCampaigns.campaigns[campaignId].value[EditCampaignValueField.experiments];

        return getArchivedExperimentsCount(experimentsValue.archivedExperimentsMap) === 0;
    }

    const activeArchivedExperimentsCount = getArchivedExperimentsCount(experimentsEditorState.archivedExperimentsMap);

    if (activeArchivedExperimentsCount !== 0) {
        return false;
    }

    if (!experimentsEditorState.areCorrectionsEnabled) {
        return true;
    }

    const hasCorrectionsErrors = values(experimentsEditorState.correctionsMap).some(correctionItem => {
        return correctionItem.errors.length !== 0;
    });

    return !hasCorrectionsErrors;
};

export const validateCampaignMetrikaCountersEditor = (campaignId: string, state: State): ValidationErrorData[] => {
    const editorId = getCampaignEditPageEditorId(campaignId);
    const { appliedCounters } = selectMetrikaCounterEditorById(state, editorId);

    return validateCampaignMetrikaCounters(appliedCounters, state.metrikaCounterEditor.counters);
};

export const validateCampaignMetrikaCounters = (
    campaignCounters: string[] = [],
    availabledForClientCounters: string[] = []
): ValidationErrorData[] => {
    const errors = [];
    const unAvailableCounters = campaignCounters.filter(counter => !availabledForClientCounters.includes(counter));

    if (unAvailableCounters.length !== 0) {
        errors.push({
            code: 'METRIKA_COUNTER_IS_UNAVAILABLE',
            text: i18n('metrika-counters-is-unavailable', {
                count: unAvailableCounters.length,
                counters: unAvailableCounters.join(', ')
            })
        });
    }

    return errors;
};

export const validateCampaignServiceEditor = (campaignId: string, state: State): ValidationErrorData[] => {
    const editorId = getCampaignServiceEditorId(campaignId);
    const editorData = selectCampaignServiceEditorData(state, editorId);

    return validateCampaignService(editorData);
};

export const validateCampaignService = (service: CampaignService): ValidationErrorData[] => {
    const { serviceId } = service;
    const errors: ValidationErrorData[] = [];

    if (!serviceId) {
        errors.push({ text: i18n('required-field') });
    }

    return errors;
};

export const validateCampaignPlaceEditor = (campaignId: string, state: State): ValidationErrorData[] => {
    const editorId = getCampaignPlaceEditorId(campaignId);
    const editorData = selectCampaignPlaceEditorData(state, editorId);

    return validateCampaignPlace(editorData);
};

export const validateCampaignPlace = (place: CampaignPlace): ValidationErrorData[] => {
    const { placeId } = place;
    const errors: ValidationErrorData[] = [];

    if (!placeId) {
        errors.push({
            code: CampaignPlaceEditorError.EMPTY_PLACE_ID,
            text: i18n('required-field')
        });
    }

    return errors;
};

export const getCampaignEditPageEditorId = (campaignId: string, suffix?: string): string => {
    return `campaign-${campaignId}${suffix ? suffix : ''}`;
};

export const getCampaignExperimentsEditorId = (campaignId: string): string => {
    return getCampaignEditPageEditorId(campaignId, EditCampaignValueField.experiments);
};

export const getCampaignDisabledIpsEditorId = (campaignId: string): string => {
    return getCampaignEditPageEditorId(campaignId, EditCampaignValueField.disabledIps);
};

export const getCampaignMinusPhrasesEditorId = (campaignId: string): string => {
    return getCampaignEditPageEditorId(campaignId, EditCampaignValueField.minusPhrases);
};

export const getCampaignBannedDomainsEditorId = (campaignId: string): string => {
    return getCampaignEditPageEditorId(campaignId, EditCampaignValueField.bannedDomains);
};

export const getCampaignBannedVideoDomainsEditorId = (campaignId: string): string => {
    return getCampaignEditPageEditorId(campaignId, EditCampaignValueField.bannedVideoDomains);
};

export const getCampaignAllowedPageIdsEditorId = (campaignId: string): string => {
    return getCampaignEditPageEditorId(campaignId, 'allowedPageIds');
};

export const getCampaignChatListEditorId = (campaignId: string): string => {
    return getCampaignEditPageEditorId(campaignId, EditCampaignValueField.chatOperator);
};

export const getCampaignMetrikaKeyGoalsEditorId = (campaignId: string): string => {
    return getCampaignEditPageEditorId(campaignId, EditCampaignValueField.metrikaKeyGoals);
};

export const getCampaignServiceEditorId = (campaignId: string): string => {
    return getCampaignEditPageEditorId(campaignId, EditCampaignValueField.service);
};

export const getCampaignPlaceEditorId = (campaignId: string): string => {
    return getCampaignEditPageEditorId(campaignId, EditCampaignValueField.place);
};

export const getCampaignStrategyEditorId = (campaignId: string): string => {
    return getCampaignEditPageEditorId(campaignId, EditCampaignValueField.strategy);
};

export const getCampaignShowsFrequencyEditorId = (campaignId: string): string => {
    return getCampaignEditPageEditorId(campaignId, EditCampaignValueField.showsFrequency);
};

export const getDefaultCampaignName = (campaignType: CampaignType, campaignsCount: number): string => {
    return i18n('name-campaign', {
        campaignName: getCampaignNameByType(campaignType),
        count: campaignsCount + 1,
        date: moment().format('DD-MM-YYYY')
    });
};

export const getCampaignNameByType = (campaignType: CampaignType): string => {
    switch (campaignType) {
        case CampaignType.TEXT:
            return i18n('tgo-campaign-name');
        case CampaignType.CONTENT_PROMOTION:
            return i18n('content-promotion-campaign-name');
        case CampaignType.CPM_PRICE:
            return i18n('price-package-campaign-name');
        case CampaignType.INTERNAL_AUTOBUDGET:
            return i18n('autobuget-campaign-name');
        default:
            throw new Error(`неподдерживаемый тип кампании: ${campaignType}`);
    }
};

export const getEnabledFieldsByType = (campaignType: CampaignType): EnabledFieldsMap => {
    const enabledFieldsByType = {
        [CampaignType.TEXT]: [
            EditCampaignValueField.name,
            EditCampaignValueField.dates,
            EditCampaignValueField.relevantPhrases,
            EditCampaignValueField.hasBannersTextSubstitution,
            EditCampaignValueField.bannersLanguage,
            EditCampaignValueField.disabledIps,
            EditCampaignValueField.chatOperator,
            EditCampaignValueField.bannedDomains,
            EditCampaignValueField.showYandexCatalogInfo,
            EditCampaignValueField.considerCompetitors,
            EditCampaignValueField.allowedPageIds,
            EditCampaignValueField.notifications,
            EditCampaignValueField.metrikaCounters,
            EditCampaignValueField.markMetrikaLinks,
            EditCampaignValueField.experiments,
            EditCampaignValueField.metrikaKeyGoals,
            EditCampaignValueField.siteMonitoring,
            EditCampaignValueField.markTargetCallLinks,
            EditCampaignValueField.strategy,
            EditCampaignValueField.hasExtendedGeoTargeting,
            EditCampaignValueField.rateCorrections,
            EditCampaignValueField.visitingCard,
            EditCampaignValueField.minusPhrases,
            EditCampaignValueField.timeTargeting,
            EditCampaignValueField.isAloneTrafaretAllowed,
            EditCampaignValueField.organizationId,
            EditCampaignValueField.regionStub
        ],
        [CampaignType.CPM_BANNER]: [
            EditCampaignValueField.name,
            EditCampaignValueField.dates,
            EditCampaignValueField.strategy,
            EditCampaignValueField.timeTargeting,
            EditCampaignValueField.rateCorrections,
            EditCampaignValueField.metrikaCounters,
            EditCampaignValueField.hasExtendedGeoTargeting,
            EditCampaignValueField.siteMonitoring,
            EditCampaignValueField.notifications,
            EditCampaignValueField.allowedPageIds,
            EditCampaignValueField.disabledIps,
            EditCampaignValueField.bannersLanguage,
            EditCampaignValueField.markTargetCallLinks,
            EditCampaignValueField.experiments,
            EditCampaignValueField.bannedDomains,
            EditCampaignValueField.regionStub,
            EditCampaignValueField.bannedVideoDomains,
            EditCampaignValueField.showsFrequency
        ],
        [CampaignType.PERFORMANCE]: [],
        [CampaignType.MCBANNER]: [],
        [CampaignType.DYNAMIC]: [],
        [CampaignType.CPM_DEALS]: [],
        [CampaignType.CONTENT_PROMOTION]: [
            EditCampaignValueField.name,
            EditCampaignValueField.timeTargeting,
            EditCampaignValueField.dates,
            EditCampaignValueField.strategy,
            EditCampaignValueField.metrikaCounters,
            EditCampaignValueField.notifications,
            EditCampaignValueField.minusPhrases,
            EditCampaignValueField.disabledIps,
            EditCampaignValueField.hasExtendedGeoTargeting,
            EditCampaignValueField.bannersLanguage,
            EditCampaignValueField.regionStub
        ],
        [CampaignType.CPM_YNDX_FRONTPAGE]: [],
        [CampaignType.INTERNAL_DISTRIB]: [],
        [CampaignType.INTERNAL_FREE]: [],
        [CampaignType.INTERNAL_AUTOBUDGET]: [],
        [CampaignType.CPM_PRICE]: [
            EditCampaignValueField.name,
            EditCampaignValueField.pricePackageId,
            EditCampaignValueField.datesWithRequiredEndDate
        ],
        [CampaignType.INTERNAL_AUTOBUDGET]: [
            EditCampaignValueField.name,
            EditCampaignValueField.dates,
            EditCampaignValueField.notifications,
            EditCampaignValueField.metrikaCounters,
            EditCampaignValueField.metrikaKeyGoals,
            EditCampaignValueField.timeTargeting,
            EditCampaignValueField.strategy,
            EditCampaignValueField.service,
            EditCampaignValueField.place,
            EditCampaignValueField.regionStub,
            EditCampaignValueField.showsFrequency
        ]
    };

    if (enabledFieldsByType[campaignType] === undefined) {
        throw new Error(`Для кампании типа ${campaignType} не указаны редактируемые поля`);
    }

    return enabledFieldsByType[campaignType].reduce((res: EnabledFieldsMap, field: EditCampaignValueField) => {
        if (field) {
            res[field] = true;
        }

        return res;
    }, {});
};

export const getCampaignsIdsWithEnabledExperiments = (campaignsValue: Record<string, EditCampaignData>): string[] => {
    return Object.keys(campaignsValue).filter(campaignId => {
        return campaignsValue[campaignId].enabledFields.experiments === true;
    });
};

export function isRelevantPhrasesEnabledByCampaignSettings(campaignData: EditCampaignData): boolean {
    return campaignData.value[EditCampaignValueField.strategy].platform !== CampaignPlatform.CONTEXT;
}

export function isAloneTrafaretAllowedEnabledByCampaignSettings(campaignData: EditCampaignData): boolean {
    return campaignData.value[EditCampaignValueField.strategy].platform !== CampaignPlatform.CONTEXT;
}

export const isStoppedByArchivedExperimentsSegments = memoize(
    (campaignData: EditCampaignData, isCampaignStopped: boolean): boolean => {
        const experementsValue = campaignData.value[EditCampaignValueField.experiments];

        return (
            isCampaignStopped &&
            experementsValue.isSegmentTargetingEnabled &&
            values(experementsValue.archivedExperimentsMap).some(isArchived => isArchived === true)
        );
    }
);

export const getNextLevelErrorsForConvertToFlatStructure = (
    errorsLevel: object,
    path: string
): ValidationErrorData[] => {
    return Object.keys(errorsLevel).reduce((acc: ValidationErrorData[], key: string) => {
        if (key === 'errors') {
            const errors = errorsLevel[key] || ([] as ValidationErrorData[]);
            acc = acc.concat(
                errors.map((errorItem: ValidationErrorData) => {
                    return {
                        ...errorItem,
                        path: path
                    };
                })
            );
        } else {
            const isParseableToInt = !isNaN(parseInt(key, 10));
            const addingKey = isParseableToInt ? `[${key}]` : key;
            let newPath = '';
            if (path && !isParseableToInt) {
                newPath = path + '.' + addingKey;
            } else {
                newPath = path + addingKey;
            }
            acc = acc.concat(getNextLevelErrorsForConvertToFlatStructure(errorsLevel[key], newPath));
        }

        return acc;
    }, []);
};

export const convertToFlatStructureErrors = (errorsData: object): ValidationErrorData[] => {
    return getNextLevelErrorsForConvertToFlatStructure(errorsData, '');
};

export function getCampaignsIdsWithErrorsInAdditionalSettings(
    editorsErrors: CampaignsEditEditorsValidationResult[]
): string[] {
    return uniq(
        editorsErrors.reduce((acc: string[], editorError) => {
            if (editorError.editor !== 'general' && isEditorInAdditionalSettings(editorError.editor)) {
                acc.push(editorError.campaignId);
            }

            return acc;
        }, [])
    );
}

export function isEditorInAdditionalSettings(editor: EditCampaignEditor): boolean {
    return [
        EditCampaignEditor.visitingCardEditor,
        EditCampaignEditor.minusPhrases,
        EditCampaignEditor.experimentsEditor,
        EditCampaignEditor.notificationsEditor,
        EditCampaignEditor.bannedDomainsEditor,
        EditCampaignEditor.disabledIpsEditor,
        EditCampaignEditor.relevantPhrasesEditor,
        EditCampaignEditor.allowedPageIdsEditor,
        EditCampaignEditor.bannersLanguage
    ].includes(editor);
}

export function getEnabledFieldsFromAtLeastOneCampaign(
    campaignsData: Record<string, EditCampaignData>
): EnabledFieldsMap {
    return values(campaignsData).reduce((acc: EnabledFieldsMap, campaignData) => {
        keys(campaignData.enabledFields).forEach(enabledFieldKey => {
            if (campaignData.enabledFields[enabledFieldKey]) {
                acc[enabledFieldKey] = true;
            }
        });

        return acc;
    }, {});
}

export const mapPartialDataByFieldFromCampaigns = <T>(
    campaignsRecord: Record<string, EditCampaignData>,
    field: EditCampaignValueField,
    mapperFunc: (campaign: EditCampaignData) => T
): T[] => {
    return Object.keys(campaignsRecord).reduce((acc: T[], campaignId) => {
        const currentCampaign = campaignsRecord[campaignId];
        if (currentCampaign.enabledFields[field]) {
            acc.push(mapperFunc(currentCampaign));
        }

        return acc;
    }, []);
};

export const isStrategyNeedsMarkMetrikaLinksEnabled = (strategy?: StrategyForEditor): boolean => {
    return isStrategyOptimizeCpa(strategy) && strategy.isCpaPayForConversionEnabled;
};
